import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { NL_AREAS, findNlArea } from "@/lib/riviera";
import { RivieraHero, BTN_GHOST } from "@/components/riviera/RivieraUI";
import { BTN_GREEN } from "@/pages/public/Home";
import { usePageMeta } from "@/lib/seo";
import { CityServices, CityInclusions, CityHowItWorks } from "@/components/nl/CitySections";

const EYEBROW = "text-xs uppercase tracking-[0.22em] font-bold text-[#5BA83F]";

export default function NlCity() {
  const { city: slug } = useParams();
  const { t } = useTranslation();
  const area = findNlArea(slug);
  const headline = area ? t(`nl_cities.${area.slug}.headline`) : "";
  usePageMeta(area ? `${headline} | Trustline Services NL` : null, area ? t(`nl_cities.${area.slug}.intro`) : "", area ? {
    canonicalPath: `/service-areas/${area.slug}`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service", serviceType: "Cleaning services", name: headline,
      areaServed: { "@type": "City", name: area.city, containedInPlace: { "@type": "Country", name: "Netherlands" } },
      provider: {
        "@type": "LocalBusiness", name: "Trustline Services NL", telephone: "+31642449019", email: "info@trustlineservices.nl",
        address: { "@type": "PostalAddress", streetAddress: "Julianastraat 52B", postalCode: "2405 CJ", addressLocality: "Alphen aan den Rijn", addressCountry: "NL" },
      },
    },
  } : {});
  if (!area) return <Navigate to="/service-areas" replace />;
  const k = `nl_cities.${area.slug}`;
  const ctas = (sfx = "") => (
    <>
      <Link to={`/book?city=${area.slug}`} className={BTN_GREEN} data-testid={`nl-city-book${sfx}`}>{t("nav.book_now")}</Link>
      <Link to="/quote" className={BTN_GHOST} data-testid={`nl-city-quote${sfx}`}>{t("home_v3.cta_quote")}</Link>
    </>
  );

  return (
    <div data-testid={`nl-city-${area.slug}`}>
      <RivieraHero img={area.image} alt={t("nl_city_page.hero_alt", { city: area.city })} eyebrow={`${t("nl_city_page.eyebrow")} · ${area.city}`} title={t(`${k}.headline`)} sub={t(`${k}.intro`)}>
        {ctas()}
      </RivieraHero>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <Link to="/service-areas" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="nl-city-back">
          <ArrowLeft className="w-4 h-4" /> {t("nl_city_page.back")}
        </Link>
      </div>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className={EYEBROW}>{area.city}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight mt-3">{t("nl_city_page.highlights_title", { city: area.city })}</h2>
            <p className="mt-6 text-xs font-bold uppercase tracking-widest text-slate-500">{t("nl_city_page.neighbourhoods")}</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="nl-city-neighbourhoods">
              {area.neighborhoods.split(", ").map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white ring-1 ring-slate-200 text-sm text-slate-700">
                  <MapPin className="w-3.5 h-3.5 text-[#5BA83F]" /> {n}
                </span>
              ))}
            </div>
          </div>
          <ul className="lg:col-span-7 grid gap-4 h-fit">
            {(t(`${k}.highlights`, { returnObjects: true }) || []).map((h, i) => (
              <li key={i} className="flex gap-3 items-start rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid={`nl-city-highlight-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#5BA83F] shrink-0 mt-0.5" /><span className="font-semibold text-[#0F172A]">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CityServices t={t} area={area} />
      <CityInclusions t={t} />
      <CityHowItWorks t={t} />

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="text-3xl sm:text-4xl text-[#0F172A] text-center">{t("nl_city_page.faq_title", { city: area.city })}</h2>
          <Accordion type="single" collapsible className="mt-8 bg-white rounded-2xl ring-1 ring-slate-200 px-6">
            {(t(`${k}.faq`, { returnObjects: true }) || []).map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} data-testid="faq-accordion-item">
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="pt-4 md:pt-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="bg-[#102547] rounded-3xl px-8 py-12 md:px-14 text-white relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-[#5BA83F]/25 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl max-w-2xl">{t("nl_city_page.cta_title", { city: area.city })}</h2>
              <p className="mt-3 text-slate-300 max-w-xl">{t("nl_city_page.cta_sub")}</p>
              <div className="mt-8 flex flex-wrap gap-3">{ctas("-bottom")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-12" data-testid="nl-city-others">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t("nl_city_page.back")}</p>
          <div className="flex flex-wrap gap-2">
            {NL_AREAS.filter((a) => a.slug !== area.slug).map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} data-testid={`nl-city-other-${a.slug}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white ring-1 ring-slate-200 text-sm text-slate-700 hover:bg-[#E8F5E4]/60 transition-colors">
                <MapPin className="w-3.5 h-3.5 text-[#5BA83F]" /> {a.city}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
